import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AssetModuleNavbar from "./AssetModuleNavbar";
import API_BASE_URL from "../config";
import SystemNotes from "./SystemNotes";

const Field = ({ label, value, mono }) => (
  <div className="col-md-3 col-6 mb-3">
    <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 14, color: "#1a1f36", fontFamily: mono ? "monospace" : "inherit", fontWeight: mono ? 600 : 400 }}>{value || "—"}</div>
  </div>
);

const Section = ({ title, icon, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-3 border rounded">
      <div className="fw-bold p-2 border-bottom d-flex justify-content-between align-items-center"
        style={{ cursor: "pointer", backgroundColor: "#DFE4EB", borderRadius: "4px 4px 0 0" }}
        onClick={() => setOpen(!open)}>
        <span><i className={`bi ${icon} me-2`}></i>{title}</span>
        <span>{open ? "▾" : "▸"}</span>
      </div>
      {open && <div className="row g-0 p-3">{children}</div>}
    </div>
  );
};

export default function ViewAllocation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [allocation, setAllocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [returning, setReturning] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [activeTab, setActiveTab] = useState("systemNotes");
  const [showReturnForm, setShowReturnForm] = useState(false);
  const [navbarColor, setNavbarColor] = useState("#080235");
  const [returnForm, setReturnForm] = useState({
    returnDate: new Date().toISOString().split("T")[0],
    returnRemarks: "",
    conditionAtReturn: "Good",
  });

  useEffect(() => {
    const bl = document.createElement("link"); bl.rel = "stylesheet"; bl.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css"; document.head.appendChild(bl);
    const ic = document.createElement("link"); ic.rel = "stylesheet"; ic.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css"; document.head.appendChild(ic);
    return () => { bl.remove(); ic.remove(); };
  }, []);

  useEffect(() => {
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
    // if (!roles.includes("ROLE_ADMIN")) { toast.error("Unauthorized."); navigate("/"); }
  }, [navigate, token]);

  useEffect(() => {
        const userId = localStorage.getItem("userId");
        if (!token || !userId) return;
        fetch(`${API_BASE_URL}/api/appearance/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.json()).then(d => { if (d?.navbarColor) setNavbarColor(d.navbarColor); }).catch(() => { });
    }, [token]);

  useEffect(() => {
  if (!token || !id) return;
  setLoading(true);
  fetch(`${API_BASE_URL}/api/asset-allocations/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then(r => { if (!r.ok) throw new Error("Not found"); return r.json(); })
    .then(setAllocation)
    .catch(() => { toast.error("Failed to load allocation."); navigate("/allallocations"); })
    .finally(() => setLoading(false));
}, [id, token, navigate]);


  const handleReturn = async () => {
    if (!returnForm.returnDate) { toast.error("Return date is required."); return; }
    setReturning(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/asset-allocations/${id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ status: "RETURNED", ...returnForm }),
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success("Asset returned successfully.");
      setAllocation(prev => ({ ...prev, status: "RETURNED", ...returnForm }));
      setShowReturnForm(false);
    } catch (err) { toast.error(err.message || "Failed to process return."); }
    finally { setReturning(false); }
  };

  if (loading) return (
    <>
      <div className="fixed-top"><AssetModuleNavbar /></div>
      <div className="container-fluid" style={{ paddingTop: 110 }}>
        <div className="text-center py-5">
          <div className="spinner-border text-primary"></div>
          <div className="text-muted mt-2" style={{ fontSize: 13 }}>Loading allocation...</div>
        </div>
      </div>
    </>
  );

  if (!allocation) return null;

  const isActive = allocation.status === "ACTIVE";
  const statusColor = isActive ? "#198754" : "#6b7280";

  return (
    <>
      <ToastContainer position="top-right" autoClose={4000} />
      <div className="fixed-top"><AssetModuleNavbar /></div>
      <div className="container-fluid my-4" style={{ paddingTop: "90px" }}>

        {/* Header */}
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <h4 className="fw-bold mb-0"><i className="bi bi-person-check me-2"></i>Allocation Detail</h4>
              <span className="badge" style={{ background: isActive ? "#dcfce7" : "#f3f4f6", color: statusColor, border: `1px solid ${isActive ? "#86efac" : "#d1d5db"}`, fontSize: 12 }}>
                {allocation.status}
              </span>
              {isActive && !allocation.acknowledged && (
                <span className="badge bg-warning text-dark" style={{ fontSize: 11 }}>OTP Pending</span>
              )}
              {isActive && allocation.acknowledged && (
                <span className="badge bg-success" style={{ fontSize: 11 }}>✓ Acknowledged</span>
              )}
            </div>
            <div className="d-flex gap-2 mt-1">
              <button className="btn btn-primary btn-sm" onClick={() => navigate(`/editallocation/${id}`)}>
                 Edit
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>
                 Back
              </button>
            </div>
          </div>
          <div className="d-flex gap-2">
            {isActive && (
              <button className="btn btn-warning btn-sm" onClick={() => setShowReturnForm(r => !r)}>
                <i className="bi bi-arrow-return-left me-1"></i> Return Asset
              </button>
            )}
            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(`/viewasset/${allocation.assetId}`)}>
              <i className="bi bi-box-seam me-1"></i> View Asset
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate("/allallocations")}>
              <i className="bi bi-list-ul me-1"></i> All Allocations
            </button>
          </div>
        </div>

        {/* Return Form */}
        {showReturnForm && (
          <div className="border rounded p-3 mb-3" style={{ background: "#fffbeb", borderColor: "#fcd34d" }}>
            <h6 className="fw-bold mb-3"><i className="bi bi-arrow-return-left me-2 text-warning"></i>Process Asset Return</h6>
            <div className="row g-3">
              <div className="col-md-3">
                <label className="form-label">Return Date <span className="text-danger">*</span></label>
                <input type="date" className="form-control form-control-sm" value={returnForm.returnDate}
                  onChange={e => setReturnForm(p => ({ ...p, returnDate: e.target.value }))} />
              </div>
              <div className="col-md-3">
                <label className="form-label">Condition at Return</label>
                <select className="form-select form-select-sm" value={returnForm.conditionAtReturn}
                  onChange={e => setReturnForm(p => ({ ...p, conditionAtReturn: e.target.value }))}>
                  <option>Good</option><option>Fair</option><option>Damaged</option>
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Return Remarks</label>
                <input type="text" className="form-control form-control-sm" value={returnForm.returnRemarks}
                  onChange={e => setReturnForm(p => ({ ...p, returnRemarks: e.target.value }))}
                  placeholder="Condition notes, missing accessories, damage..." />
              </div>
              <div className="col-12 d-flex gap-2">
                <button className="btn btn-warning btn-sm" onClick={handleReturn} disabled={returning}>
                  {returning ? <><span className="spinner-border spinner-border-sm me-1"></span>Processing...</> : "Confirm Return"}
                </button>
                <button className="btn btn-outline-secondary btn-sm" onClick={() => setShowReturnForm(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        <Section title="Employee" icon="bi-person">
          <Field label="Employee Name" value={allocation.employeeName} />
        </Section>

        <Section title="Asset" icon="bi-laptop">
          <Field label="Asset Code" value={allocation.assetCode} mono />
          <Field label="Asset Name" value={allocation.assetName} />
        </Section>

        <Section title="Allocation Details" icon="bi-info-circle">
          <Field label="Allocation Type" value={allocation.allocationType} />
          <Field label="Allocation Date" value={allocation.allocationDate} />
          <Field label="Expected Return Date" value={allocation.expectedReturnDate} />
          <Field label="Condition at Issue" value={allocation.conditionAtIssue} />
          <Field label="Acknowledged" value={allocation.acknowledged ? "Yes — Confirmed" : "No — Pending"} />
          <Field label="Handover Remarks" value={allocation.handoverRemarks} />
        </Section>

        {allocation.status === "RETURNED" && (
          <Section title="Return Details" icon="bi-arrow-return-left">
            <Field label="Return Date" value={allocation.returnDate} />
            <Field label="Condition at Return" value={allocation.conditionAtReturn} />
            <Field label="Return Remarks" value={allocation.returnRemarks} />
          </Section>
        )}

        {/* System Information Tabs */}
        <ul className="nav nav-tabs mb-0 fw-semibold mt-3" style={{ backgroundColor: navbarColor }}>
          {[["systemNotes", "System Notes"]].map(([key, label]) => (
            <li className="nav-item" key={key}>
              <button
                type="button"
                className={`nav-link ${activeTab === key ? "active" : ""}`}
                onClick={() => setActiveTab(key)}
                style={{
                  color: activeTab === key ? navbarColor : "#fff",
                  backgroundColor: activeTab === key ? "#fff" : "transparent",
                  fontWeight: activeTab === key ? "600" : "normal",
                }}
              >
                {label}
              </button>
            </li>
          ))}
        </ul>
        <div className="border p-3">
          <div className="text-muted small mb-2">
            <strong>Date Created:</strong>{" "}
            {allocation.createdAt ? new Date(allocation.createdAt).toLocaleString() : "—"}
          </div>
          {activeTab === "systemNotes" && (
            <SystemNotes entityType="ALLOCATION" entityId={allocation.id} token={token} />
          )}
        </div>
      </div>
    </>
  );
}
